import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { ArrowBack as BackIcon, Save as SaveIcon } from '@mui/icons-material';
import { usePoliticasVendedor } from '../hooks/usePoliticasVendedor';
import { DescontoForm } from '../components/DescontoForm';
import { TabelaPrecoForm } from '../components/TabelaPrecoForm';
import { CondicoesPagtoForm } from '../components/CondicoesPagtoForm';
import { ProdutosBlacklist } from '../components/ProdutosBlacklist';
import { useAdminLookups } from '../hooks/useAdminUsuarios';

// AIDEV-NOTE: Página de configuração por vendedor com 4 abas:
// Comercial (descontos, cadastro, sync), Tabela de Preço (regras por quantidade),
// Cond. Pagamento (restrições), Produtos (blacklist por grupo/subgrupo/produto)

export function VendedorConfigPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const vendedorId = Number(id);
  const [tab, setTab] = useState(0);
  const [successMsg, setSuccessMsg] = useState('');
  const [localOverride, setLocalOverride] = useState<Record<string, unknown>>({});
  const [localRegras, setLocalRegras] = useState<any[] | null>(null);
  const [localRestricoesPagto, setLocalRestricoesPagto] = useState<any[] | null>(null);

  const {
    politicasQuery,
    regrasQuery,
    restricoesPagtoQuery,
    restricoesProdutosQuery,
    savePoliticasMutation,
    saveRegrasMutation,
    saveRestricoesPagtoMutation,
    addRestricaoProdutoMutation,
    removeRestricaoProdutoMutation
  } = usePoliticasVendedor(vendedorId);

  const {
    tabelas,
    condicoes,
    origens,
    grupos,
    tiposComercializacao,
    tiposMovimentoReceber,
    pontosEstoque
  } = useAdminLookups();

  const globalPoliticas = politicasQuery.data?.global;
  const overridePoliticas = { ...(politicasQuery.data?.override ?? {}), ...localOverride };

  const handleSavePoliticas = async () => {
    await savePoliticasMutation.mutateAsync(localOverride as any);
    setLocalOverride({});
    setSuccessMsg('Políticas do vendedor salvas!');
  };

  const handleSaveRegras = async () => {
    await saveRegrasMutation.mutateAsync(localRegras ?? []);
    setLocalRegras(null);
    setSuccessMsg('Regras de tabela salvas!');
  };

  const handleSaveRestricoesPagto = async () => {
    await saveRestricoesPagtoMutation.mutateAsync(localRestricoesPagto ?? []);
    setLocalRestricoesPagto(null);
    setSuccessMsg('Restrições de pagamento salvas!');
  };

  if (politicasQuery.isLoading) return <CircularProgress />;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/usuarios')} size="small">
          Voltar
        </Button>
        <Typography variant="h5">Configurações do Vendedor #{vendedorId}</Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Comercial" />
        <Tab label="Tabela de Preço" />
        <Tab label="Cond. Pagamento" />
        <Tab label="Produtos" />
      </Tabs>

      <Paper sx={{ p: 2 }}>
        {/* Aba Comercial — descontos + cadastro + sync */}
        {tab === 0 && (
          <>
            <DescontoForm
              title="Descontos"
              podeDescontoItem={
                (overridePoliticas.pode_dar_desconto_item as boolean | null) ?? null
              }
              limiteDescontoItem={(overridePoliticas.limite_desconto_item as number | null) ?? null}
              podeDescontoTotal={
                (overridePoliticas.pode_dar_desconto_total as boolean | null) ?? null
              }
              limiteDescontoTotal={
                (overridePoliticas.limite_desconto_total as number | null) ?? null
              }
              podeAlterarPreco={
                (overridePoliticas.pode_alterar_preco_unitario as boolean | null) ?? null
              }
              limiteReducaoPreco={
                (overridePoliticas.limite_reducao_preco_pct as number | null) ?? null
              }
              limiteAumentoPreco={
                (overridePoliticas.limite_aumento_preco_pct as number | null) ?? null
              }
              isOverride={true}
              globalValues={{
                podeDescontoItem: globalPoliticas?.pode_dar_desconto_item ?? false,
                limiteDescontoItem: globalPoliticas?.limite_desconto_item ?? 0,
                podeDescontoTotal: globalPoliticas?.pode_dar_desconto_total ?? false,
                limiteDescontoTotal: globalPoliticas?.limite_desconto_total ?? 0,
                podeAlterarPreco: globalPoliticas?.pode_alterar_preco_unitario ?? false,
                limiteReducaoPreco: globalPoliticas?.limite_reducao_preco_pct ?? null,
                limiteAumentoPreco: globalPoliticas?.limite_aumento_preco_pct ?? null
              }}
              onChange={(field, value) => setLocalOverride((prev) => ({ ...prev, [field]: value }))}
            />
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={
                      (overridePoliticas.pode_cadastrar_clientes as boolean | null) ??
                      globalPoliticas?.pode_cadastrar_clientes ??
                      false
                    }
                    onChange={(e) =>
                      setLocalOverride((prev) => ({
                        ...prev,
                        pode_cadastrar_clientes: e.target.checked
                      }))
                    }
                  />
                }
                label="Pode cadastrar clientes"
              />
              <FormControl size="small" sx={{ ml: 2, minWidth: 180 }}>
                <InputLabel>Sync Pedidos</InputLabel>
                <Select
                  value={
                    (overridePoliticas.config_sync_pedido as string | null) ??
                    globalPoliticas?.config_sync_pedido ??
                    'manual'
                  }
                  label="Sync Pedidos"
                  onChange={(e) =>
                    setLocalOverride((prev) => ({ ...prev, config_sync_pedido: e.target.value }))
                  }
                >
                  <MenuItem value="manual">Manual</MenuItem>
                  <MenuItem value="automatico">Automático</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel>Tabela de Preco Padrao</InputLabel>
                <Select
                  value={
                    ((overridePoliticas.tabela_preco_padrao_id as number | null) ??
                      globalPoliticas?.tabela_preco_padrao_id ??
                      '') as number | ''
                  }
                  label="Tabela de Preco Padrao"
                  onChange={(e) =>
                    setLocalOverride((prev) => ({
                      ...prev,
                      tabela_preco_padrao_id: e.target.value === '' ? null : Number(e.target.value)
                    }))
                  }
                >
                  <MenuItem value="">— usar padrão global —</MenuItem>
                  {(tabelas.data ?? []).map((t) => (
                    <MenuItem key={t.codigo} value={t.codigo}>
                      {t.codigo} — {t.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel>Origem Padrao do Pedido</InputLabel>
                <Select
                  value={
                    ((overridePoliticas.origem_padrao_id as number | null) ??
                      globalPoliticas?.origem_padrao_id ??
                      '') as number | ''
                  }
                  label="Origem Padrao do Pedido"
                  onChange={(e) =>
                    setLocalOverride((prev) => ({
                      ...prev,
                      origem_padrao_id: e.target.value === '' ? null : Number(e.target.value)
                    }))
                  }
                >
                  <MenuItem value="">Nenhuma (campo vazio)</MenuItem>
                  {(origens.data ?? []).map((o) => (
                    <MenuItem key={o.codigo} value={o.codigo}>
                      {o.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel>Ponto de Estoque</InputLabel>
                <Select
                  value={
                    ((overridePoliticas.ponto_estoque_id as number | null) ??
                      globalPoliticas?.ponto_estoque_id ??
                      '') as number | ''
                  }
                  label="Ponto de Estoque"
                  onChange={(e) =>
                    setLocalOverride((prev) => ({
                      ...prev,
                      ponto_estoque_id: e.target.value === '' ? null : Number(e.target.value)
                    }))
                  }
                >
                  <MenuItem value="">Herdar do global</MenuItem>
                  {(pontosEstoque.data ?? []).map((p) => (
                    <MenuItem key={p.codigo} value={p.codigo}>
                      {p.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel>Tipo de Comercializacao Padrao</InputLabel>
                <Select
                  value={
                    ((overridePoliticas.tipo_comercializacao_padrao_id as number | null) ??
                      globalPoliticas?.tipo_comercializacao_padrao_id ??
                      '') as number | ''
                  }
                  label="Tipo de Comercializacao Padrao"
                  onChange={(e) =>
                    setLocalOverride((prev) => ({
                      ...prev,
                      tipo_comercializacao_padrao_id:
                        e.target.value === '' ? null : Number(e.target.value)
                    }))
                  }
                >
                  <MenuItem value="">Nenhum (padrao do ERP)</MenuItem>
                  {(tiposComercializacao.data ?? []).map((t) => (
                    <MenuItem key={t.codigo} value={t.codigo}>
                      {t.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel>Tipo Movimento Receber Padrao</InputLabel>
                <Select
                  value={
                    ((overridePoliticas.tipo_movimento_receber_padrao_id as number | null) ??
                      globalPoliticas?.tipo_movimento_receber_padrao_id ??
                      '') as number | ''
                  }
                  label="Tipo Movimento Receber Padrao"
                  onChange={(e) =>
                    setLocalOverride((prev) => ({
                      ...prev,
                      tipo_movimento_receber_padrao_id:
                        e.target.value === '' ? null : Number(e.target.value)
                    }))
                  }
                >
                  <MenuItem value="">Nenhum (padrao do ERP)</MenuItem>
                  {(tiposMovimentoReceber.data ?? []).map((t) => (
                    <MenuItem key={t.codigo} value={t.codigo}>
                      {t.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSavePoliticas}
                disabled={savePoliticasMutation.isPending}
              >
                Salvar
              </Button>
            </Box>
          </>
        )}

        {/* Aba Tabela de Preço — regras de auto-seleção por quantidade */}
        {tab === 1 && (
          <>
            {regrasQuery.isLoading ? (
              <CircularProgress size={20} />
            ) : (
              <TabelaPrecoForm
                regras={localRegras ?? regrasQuery.data ?? []}
                tabelas={tabelas.data ?? []}
                onChange={setLocalRegras}
              />
            )}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveRegras}
                disabled={saveRegrasMutation.isPending || localRegras === null}
              >
                Salvar Regras
              </Button>
            </Box>
          </>
        )}

        {/* Aba Condições de Pagamento — restrições e valor mínimo */}
        {tab === 2 && (
          <>
            {restricoesPagtoQuery.isLoading || condicoes.isLoading ? (
              <CircularProgress size={20} />
            ) : (
              <CondicoesPagtoForm
                condicoes={condicoes.data ?? []}
                restricoes={localRestricoesPagto ?? restricoesPagtoQuery.data ?? []}
                onChange={setLocalRestricoesPagto}
              />
            )}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveRestricoesPagto}
                disabled={saveRestricoesPagtoMutation.isPending || localRestricoesPagto === null}
              >
                Salvar
              </Button>
            </Box>
          </>
        )}

        {/* Aba Produtos — blacklist por grupo/subgrupo/produto */}
        {tab === 3 && (
          <>
            {restricoesProdutosQuery.isLoading ? (
              <CircularProgress size={20} />
            ) : (
              <ProdutosBlacklist
                restricoes={restricoesProdutosQuery.data ?? []}
                grupos={grupos.data?.grupos ?? []}
                subgrupos={grupos.data?.subgrupos ?? []}
                onAdd={(payload) => addRestricaoProdutoMutation.mutate(payload)}
                onRemove={(id) => removeRestricaoProdutoMutation.mutate(id)}
                isLoading={
                  addRestricaoProdutoMutation.isPending || removeRestricaoProdutoMutation.isPending
                }
              />
            )}
            {restricoesProdutosQuery.error && (
              <Alert severity="error">Erro ao carregar restrições</Alert>
            )}
          </>
        )}
      </Paper>

      <Snackbar
        open={!!successMsg}
        autoHideDuration={3000}
        onClose={() => setSuccessMsg('')}
        message={successMsg}
      />
    </Box>
  );
}
